import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fetchJson } from "../../services/api";
import { InsightsData } from "../types";
import { PageHeader, SectionCard, Loader, ErrorMsg, refreshBtnStyle } from "../components/Common";
import "./InsightsPage.css";

const PIE_COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#a78bfa", "#06b6d4"];

const InsightsPage: React.FC = () => {
  const [data, setData]       = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    fetchJson<InsightsData>("/monitor/insights/", { top: 8 })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;
  if (error)   return <ErrorMsg msg={error} onRetry={load} />;
  if (!data)   return null;

  return (
    <div>
      <PageHeader title="Insights" subtitle="Usage breakdown by school, family, user type and method" />
      <div className="insights-page__toolbar">
        <button onClick={load} style={refreshBtnStyle}>↻ Refresh</button>
      </div>

      <div className="insights-page__grid">
        <SectionCard title="Top Schools by API Usage">
          {data.by_school.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_school} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="school_code" tick={{ fill: "#94a3b8", fontSize: 11 }} width={70} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={ttStyle} formatter={(v) => [v, "Calls"]} />
                <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Most Active Families">
          {data.by_family.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_family} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="family_code" tick={{ fill: "#94a3b8", fontSize: 11 }} width={70} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={ttStyle} formatter={(v) => [v, "Calls"]} />
                <Bar dataKey="count" fill="#a78bfa" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>

      <div className="insights-page__grid--3">
        <SectionCard title="Calls by User Type">
          {data.by_user_type.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data.by_user_type}
                  dataKey="count"
                  nameKey="user_type"
                  cx="50%" cy="50%"
                  outerRadius={70}
                  label={({ user_type, percent }: { user_type?: string; percent?: number }) => `${user_type ?? ""} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {data.by_user_type.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={ttStyle} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Calls by HTTP Method">
          {data.by_method.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data.by_method}
                  dataKey="count"
                  nameKey="method"
                  cx="50%" cy="50%"
                  outerRadius={70}
                  label={({ method, percent }: { method?: string; percent?: number }) => `${method ?? ""} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {data.by_method.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={ttStyle} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Top Endpoints">
          {data.by_endpoint.length === 0 ? <Empty /> : (
            <table className="insights-page__table">
              <tbody>
                {data.by_endpoint.map((row) => (
                  <tr key={row.endpoint} className="insights-page__table-row">
                    <td className="insights-page__table-cell">{row.endpoint}</td>
                    <td className="insights-page__table-value">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default InsightsPage;

const ttStyle: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text)" };
const Empty: React.FC = () => <div className="insights-page__empty">No data yet</div>;
